import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DialogActions, DialogContent, Stack, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TRecruitFormData } from 'src/@types';
import { CDownloadDialog } from 'src/components/custom-download/CDownloadDIalog';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFRadioGroup, RHFSelect, RHFTextField } from 'src/components/hook-form';
import RHFDatePicker from 'src/components/hook-form/RHFDatePicker';
import {
  ButtonContainer,
  FormInnerContainer,
  FormItem,
  FormLabel,
  FormRow,
} from 'src/layouts/form/FormLayout';
import { IRecruit, useStores } from 'src/models';
import { recruitApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FORM_IDS } from 'src/static/constants/common/form';
import { getRecruitFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import * as yup from 'yup';

export interface IRecruitDownladProps {
  recruitSid: number;
  fileNm: string;
  filePath: string[];
}
interface IRecruitFormDialogProps {
  onSubmitSuccess: () => void;
  selectedData: IRecruit;
}

export const RecruitFormDialog = observer(
  ({ onSubmitSuccess, selectedData }: IRecruitFormDialogProps) => {
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const { commonCodeStore } = useStores();
    const { openDialog } = useDialogContext();
    const { t } = useTranslation();
    // 경력여부
    const EXP_CD = commonCodeStore.getCCodeByName('EXP_CD');
    // 지원분야
    const { applySelectOptions } = commonCodeStore;
    // 보훈 여부
    const VETERAN_CD = commonCodeStore.getCCodeByName('VETERAN_CD');
    // 장애 여부
    const DISABLED_CD = commonCodeStore.getCCodeByName('DISABLED_CD');
    // 성별
    const GENDER_TYPE_CD = commonCodeStore.getCCodeByName('GENDER_TYPE_CD');
    // 양력/음력
    const CALENDAR_TYPE_CD = commonCodeStore.getCCodeByName('CALENDAR_TYPE_CD');
    // 처리상태
    const RECRUIT_STATUS_CD = commonCodeStore.getCCodeByName('RECRUIT_STATUS_CD');
    const recruitFormSchema = yup.object().shape({
      recruitSid: yup.number().nullable(),
      recruitStatusCd: yup.string().required('채용 상태를 선택해주세요.'),
      memo: yup.string().nullable(),
    });

    const methods = useForm<TRecruitFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      resolver: yupResolver(recruitFormSchema),
      defaultValues: getRecruitFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: TRecruitFormData) => {
      // 등록, 수정 함수
      const formData = {
        recruitSid: data.recruitSid,
        recruitStatusCd: data.recruitStatusCd!,
        memo: data.memo,
      };
      const result = recruitApi.put(formData);
      if (await result) {
        handleResponse(`수정이 완료되었습니다.`, `수정이 실패되었습니다.`, onSubmitSuccess);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        recruitApi
          .remove(selectedData.recruitSid!)
          .then(() =>
            handleResponse(
              '지원서 삭제가 완료되었습니다.',
              '지원서 삭제가 실패되었습니다.',
              onSubmitSuccess,
            ),
          );
      });
      alertStore.setProps({
        open: true,
        title: '지원서 삭제',
        content: '해당 지원서를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    // 지원서 다운로드 dialog 오픈
    const handleDownloadClick = (resumeFilePath: string[]) => {
      if (resumeFilePath.length === 0) {
        alertStore.setProps({
          open: true,
          title: '지원서 다운로드',
          content: '지원서가 존재하지 않습니다.',
          category: 'error',
        });
        return;
      }
      const attachment = {
        url: `/v1/recruit/${selectedData.recruitSid}`,
        sid: selectedData.recruitSid ?? 0,
        fileNm: `${selectedData.nameKr} 지원서`,
        filePath: selectedData.resumeFilePath,
      };
      openDialog({
        id: FORM_IDS.RECRUIT_DOWNLOAD,
        title: '지원서 다운로드',
        maxWidth: 'sm',
        dialogContent: <CDownloadDialog data={attachment} />,
      });
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('recruitment.form.basicInfo.expCd.name')}
                  name="expCd"
                  options={EXP_CD || []}
                  useAll={false}
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFSelect
                  label={t('recruitment.form.basicInfo.applyFieldCd.name')}
                  name="applyFieldCd"
                  options={applySelectOptions}
                  useAll={false}
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.basicInfo.desiredSalary.name')}
                  name="desiredSalary"
                  readonly={true}
                />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('recruitment.form.basicInfo.veteranCd.name')}
                  name="veteranCd"
                  options={VETERAN_CD || []}
                  useAll={false}
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFSelect
                  label={t('recruitment.form.basicInfo.disabledCd.name')}
                  name="disabledCd"
                  options={DISABLED_CD || []}
                  useAll={false}
                  readonly={true}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('recruitment.form.personalInfo.name')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.personalInfo.nameKr.name')}
                  name="nameKr"
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.personalInfo.nameEn.name')}
                  name="nameEn"
                  readonly={true}
                />
              </FormItem>
              <FormItem size={2}>
                <RHFRadioGroup
                  label={''}
                  name="genderTypeCd"
                  options={GENDER_TYPE_CD || []}
                  readonly={true}
                />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.personalInfo.email.name')}
                  name="email"
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.personalInfo.phone.name')}
                  name="phone"
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.personalInfo.emergencyContact.name')}
                  name="emergencyContact"
                  readonly={true}
                />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFDatePicker
                  label={t('recruitment.form.personalInfo.birthDate.name')}
                  name="birthDate"
                  readonly={true}
                />
              </FormItem>
              <FormItem>
                <RHFRadioGroup
                  label={''}
                  name="calendarTypeCd"
                  options={CALENDAR_TYPE_CD || []}
                  readonly={true}
                />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField
                  label={t('recruitment.form.personalInfo.address.name')}
                  name="address"
                  readonly={true}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>🚨 {t('recruitment.form.recruitStatusCd.name')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('recruitment.form.recruitStatusCd.name')}
                  name="recruitStatusCd"
                  options={RECRUIT_STATUS_CD || []}
                  useAll={false}
                />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField label={t('recruitment.form.memo.name')} name="memo" />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
        </DialogContent>
        <DialogActions>
          <ButtonContainer>
            <Button
              onClick={() => handleDownloadClick(selectedData.resumeFilePath)}
              variant="contained"
              color="success"
            >
              {t('recruitment.button.download')}
            </Button>
            <ButtonGroup>
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
              <Button type="submit" variant="contained" form={formId} color="primary">
                {t('common.button.save')}
              </Button>
            </ButtonGroup>
          </ButtonContainer>
        </DialogActions>
      </FormProvider>
    );
  },
);
const ButtonGroup = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));
